import React, { useState, useEffect } from 'react';
import Label from "components/Label/Label";
import { getCurrencySymbol, getProductCurrency, getProductTax, globalCurrentEventId } from "utils/global";
import NcInputNumber from "components/NcInputNumber";
import Prices from "components/Prices";
import { Product, PRODUCTS } from "data/data";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import ContactInfo from "./ContactInfo";
import PaymentMethod from "./PaymentMethod";
import ShippingAddress from "./ShippingAddress";
import { getLastOrder, getOrders, validateOrderStock } from "services/customer/order";
import { useLocation } from 'react-router-dom';
import { updateOrderBillingAddress } from "services/customer/order";
import toast from "react-hot-toast";

const CheckoutPage = () => {

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const orderId = searchParams.get('order'); // '19' in this case
  const [eventId, setEventId] = useState(globalCurrentEventId());

  const [tabActive, setTabActive] = useState<
    "ContactInfo" | "ShippingAddress" | "PaymentMethod"
  >("PaymentMethod");

  const [order, setOrder] = useState<any>({
    id: '',
  });
  const [orderItems, setOrderItems] = useState<any>([])
  const [isCheckoutBlocked, setIsCheckoutBlocked] = useState(false);
  const [billingAddress, setBillingAddress] = useState({
    company_name: '',
    street_number: '',
    postcode: '',
    city: '',
    country: '',
    vat_tax_id: ''
  });
  const [isBillingSaved, setIsBillingSaved] = useState(false);
  const [isSavingBilling, setIsSavingBilling] = useState(false);

  const hasValidBillingAddress = () => {
    return ['company_name', 'street_number', 'postcode', 'city', 'country']
      .every((key) => (billingAddress as any)[key]?.trim());
  };

  const getPaymentBlockMessage = () => {
    if (isCheckoutBlocked) {
      return 'Item now out of stock. Please return to basket and update quantities.';
    }
    if (!isBillingSaved) {
      return 'Please save billing address before payment.';
    }
    return '';
  };

  const isPaymentBlocked = isCheckoutBlocked || !isBillingSaved;

  const onChangeBillingAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBillingAddress((prev) => ({
      ...prev,
      [name]: value
    }));
    setIsBillingSaved(false);
  };

  const onSaveBillingAddress = async () => {
    if (!order?.id) {
      toast.error('Order not found');
      return;
    }
    if (!hasValidBillingAddress()) {
      toast.error('Please complete all required billing address fields.');
      return;
    }
    try {
      setIsSavingBilling(true);
      await updateOrderBillingAddress(order.id, billingAddress);
      setIsBillingSaved(true);
      toast.success('Billing address saved');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save billing address');
    } finally {
      setIsSavingBilling(false);
    }
  };

  const handleScrollToEl = (id: string) => {
    const element = document.getElementById(id);
    setTimeout(() => {
      element?.scrollIntoView({ behavior: "smooth" });
    }, 80);
  };

  const subTotal = (items: any[]): number => {
    const totalSum = items.reduce((sum: number, item: any) => {
      return sum + item.quantity * item.price;
    }, 0);

    return parseFloat(totalSum.toFixed(2));
  }

  const taxEstimated = (items: any[]): number => {
    const taxRate = getProductTax(order?.event);
    const totalTax = items.reduce((sum: number, item: any) => {
      return sum + item.quantity * item.price * taxRate / 100.0;
    }, 0);

    return parseFloat(totalTax.toFixed(2));
  };
  const totalPrice = (items: any[]): number => {
    const total = subTotal(items) + taxEstimated(items);
    return parseFloat(total.toFixed(2));
  };

  const renderProduct = (item: Product, index: number) => {
    const { id, image, price, name, quantity, currency } = item;

    return (
      <div key={index} className="relative flex py-7 first:pt-0 last:pb-0">
        <div className="relative h-36 w-24 sm:w-28 flex-shrink-0 overflow-hidden rounded-xl bg-slate-100">
          <img
            src={image}
            alt={name}
            className="h-full w-full object-contain object-center"
          />
          <Link to="/product-detail" className="absolute inset-0"></Link>
        </div>

        <div className="ml-3 sm:ml-6 flex flex-1 flex-col">
          <div>
            <div className="flex justify-between ">
              <div className="flex-[1.5] ">
                <h3 className="text-base font-semibold">
                  <Link to="/product-detail">{name}</Link>
                </h3>
                <div className="mt-1.5 sm:mt-2.5 flex text-sm text-slate-600 dark:text-slate-300">
                  <div className="flex items-center space-x-1.5">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M7.01 18.0001L3 13.9901C1.66 12.6501 1.66 11.32 3 9.98004L9.68 3.30005L17.03 10.6501C17.4 11.0201 17.4 11.6201 17.03 11.9901L11.01 18.0101C9.69 19.3301 8.35 19.3301 7.01 18.0001Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8.35 1.94995L9.69 3.28992"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2.07 11.92L17.19 11.26"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 22H16"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.85 15C18.85 15 17 17.01 17 18.24C17 19.26 17.83 20.09 18.85 20.09C19.87 20.09 20.7 19.26 20.7 18.24C20.7 17.01 18.85 15 18.85 15Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                    <span>{`Black`}</span>
                  </div>
                  <span className="mx-4 border-l border-slate-200 dark:border-slate-700 "></span>
                  <div className="flex items-center space-x-1.5">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M21 9V3H15"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 15V21H9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21 3L13.5 10.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10.5 13.5L3 21"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                    <span>{`2XL`}</span>
                  </div>
                </div>

                <div className="mt-3 flex justify-between w-full sm:hidden relative">
                  <select
                    name="qty"
                    id="qty"
                    className="form-select text-sm rounded-md py-1 border-slate-200 dark:border-slate-700 relative z-10 dark:bg-slate-800 "
                  >
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                    <option value="7">7</option>
                  </select>
                  <Prices
                    contentClass="py-1 px-2 md:py-1.5 md:px-2.5 text-sm font-medium h-full"
                    price={price}
                    currency={ currency }
                  />
                </div>
              </div>

              <div className="hidden flex-1 sm:flex justify-end">
                <Prices price={price} currency={currency} className="mt-0.5" />
              </div>
            </div>
          </div>

          <div className="flex mt-auto pt-4 items-end justify-between text-sm">
            <div className="hidden sm:block text-center relative">
              <NcInputNumber className="relative z-10" onChange={(value: number) => { }} defaultValue={quantity} disabled={true} />
            </div>

            {/* <a
              href="##"
              className="relative z-10 flex items-center mt-3 font-medium text-primary-6000 hover:text-primary-500 text-sm "
            >
              <span>Remove</span>
            </a> */}
          </div>
        </div>
      </div>
    );
  };

  const renderLeft = (order: any) => {
    return (
      <div className="space-y-8">

        {/* <div id="ContactInfo" className="scroll-mt-24">
          <ContactInfo
            isActive={tabActive === "ContactInfo"}
            onOpenActive={() => {
              setTabActive("ContactInfo");
              handleScrollToEl("ContactInfo");
            }}
            onCloseActive={() => {
              setTabActive("ShippingAddress");
              handleScrollToEl("ShippingAddress");
            }}
          />
        </div> */}

        {/* <div id="ShippingAddress" className="scroll-mt-24">
          <ShippingAddress
            isActive={tabActive === "ShippingAddress"}
            onOpenActive={() => {
              setTabActive("ShippingAddress");
              handleScrollToEl("ShippingAddress");
            }}
            onCloseActive={() => {
              setTabActive("PaymentMethod");
              handleScrollToEl("PaymentMethod");
            }}
          />
        </div> */}

        <div id="BillingAddress" className="scroll-mt-24 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
          <h3 className="text-slate-700 dark:text-slate-300 uppercase tracking-tight font-semibold text-sm">
            Billing Address
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="md:col-span-2">
              <Label className="text-sm">Company Name *</Label>
              <Input
                name="company_name"
                value={billingAddress.company_name}
                onChange={onChangeBillingAddress}
                className="mt-1.5"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm">Street & Number *</Label>
              <Input
                name="street_number"
                value={billingAddress.street_number}
                onChange={onChangeBillingAddress}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-sm">Postcode *</Label>
              <Input
                name="postcode"
                value={billingAddress.postcode}
                onChange={onChangeBillingAddress}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-sm">City *</Label>
              <Input
                name="city"
                value={billingAddress.city}
                onChange={onChangeBillingAddress}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-sm">Country *</Label>
              <Input
                name="country"
                value={billingAddress.country}
                onChange={onChangeBillingAddress}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-sm">VAT / Tax ID</Label>
              <Input
                name="vat_tax_id"
                value={billingAddress.vat_tax_id}
                onChange={onChangeBillingAddress}
                className="mt-1.5"
              />
            </div>
          </div>
          <div className="mt-4">
            <ButtonPrimary onClick={onSaveBillingAddress} disabled={isSavingBilling}>
              {isSavingBilling ? 'Saving...' : 'Save Billing Address'}
            </ButtonPrimary>
            {isBillingSaved && (
              <p className="text-green-600 text-sm mt-2">Billing address saved.</p>
            )}
          </div>
        </div>

        <div id="PaymentMethod" className="scroll-mt-24">
          <PaymentMethod
            isActive={tabActive === "PaymentMethod"}
            data={{
              orderId: order?.id, // orderId
              amount: Math.ceil(totalPrice(orderItems) * 100),
              currency: getProductCurrency(order?.event),
              isPaymentBlocked,
              paymentBlockMessage: getPaymentBlockMessage(),
              meta: {
                orderId: order?.id
              }
            }}
            onOpenActive={() => {
              setTabActive("PaymentMethod");
              handleScrollToEl("PaymentMethod");
            }}
            onCloseActive={() => setTabActive("PaymentMethod")}
          />
        </div>
      </div>
    );
  };


  useEffect(() => {
    const fetchData = async () => {
      const res = await getLastOrder('pending', eventId);
      if (res) {
        setOrder(res.order);
        const existingBillingAddress = res.order?.billing_address || {};
        setBillingAddress({
          company_name: existingBillingAddress.company_name || '',
          street_number: existingBillingAddress.street_number || '',
          postcode: existingBillingAddress.postcode || '',
          city: existingBillingAddress.city || '',
          country: existingBillingAddress.country || '',
          vat_tax_id: existingBillingAddress.vat_tax_id || ''
        });
        setIsBillingSaved(
          !!(
            existingBillingAddress.company_name &&
            existingBillingAddress.street_number &&
            existingBillingAddress.postcode &&
            existingBillingAddress.city &&
            existingBillingAddress.country
          )
        );
        setOrderItems(res?.orderItems?.map((orderItem: any) => ({
          id: orderItem.product.id, // orderItem.id
          name: orderItem.product.name,
          description: orderItem.product.description,
          // productId: orderItem.product.id,
          image: `${process.env.REACT_APP_BACKEND_URL}/${orderItem.product.image_url}`,
          category: "Category 1",
          tags: ["tag1", "tag2"],
          link: "#",
          variants: 'None',
          variantType: "image",
          sizes: ["XS", "S", "M", "L", "XL"],
          allOfSizes: ["XS", "S", "M", "L", "XL", "2XL", "3XL"],
          status: "New in",
          price: parseFloat(orderItem.price),
          quantity: orderItem.quantity,
          currency: getCurrencySymbol(getProductCurrency(res?.order?.event)) 
          
        })));
        const validation = await validateOrderStock(res.order?.id || 0);
        setIsCheckoutBlocked(validation?.is_valid === false);
      }
    }

    fetchData();

  }, [eventId]);
  return (
    <div className="nc-CheckoutPage">
      <Helmet>
        <title>Checkout || TorchEvents</title>
      </Helmet>

      <main className="container py-8 lg:pb-24 lg:pt-16 ">
        {isCheckoutBlocked && (
          <div className="mb-6 rounded-md border border-red-300 bg-red-50 p-4 text-red-700">
            Item now out of stock. Please return to basket and update quantities.
          </div>
        )}
        {!isCheckoutBlocked && !isBillingSaved && (
          <div className="mb-6 rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-700">
            Please save billing address before completing payment.
          </div>
        )}
        <div className="mb-16">
          <h2 className="block text-2xl sm:text-3xl lg:text-4xl font-semibold ">
            Checkout
          </h2>
          <div className="block mt-3 sm:mt-5 text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-400">
            <Link to={"/#"} className="">
              Home
            </Link>
            <span className="text-xs mx-1 sm:mx-1.5">/</span>
            <Link to={"/#"} className="">
              Exhibitor
            </Link>
            <span className="text-xs mx-1 sm:mx-1.5">/</span>
            <span className="underline">Checkout</span>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row">
          <div className="flex-1">{renderLeft(order)}</div>

          <div className="flex-shrink-0 border-t lg:border-t-0 lg:border-l border-slate-200 dark:border-slate-700 my-10 lg:my-0 lg:mx-10 xl:lg:mx-14 2xl:mx-16 "></div>

          <div className="w-full lg:w-[36%] ">
            <h3 className="text-lg font-semibold">Order summary</h3>
            <div className="mt-5 pt-1 text-sm text-slate-500 dark:text-slate-400 border-t border-slate-200/70 dark:border-slate-700 ">
              {/* <div>
                <Label className="text-sm">Discount code</Label>
                <div className="flex mt-1.5">
                  <Input sizeClass="h-10 px-4 py-3" className="flex-1" />
                  <button className="text-neutral-700 dark:text-neutral-200 border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-100 rounded-2xl px-4 ml-3 font-medium text-sm bg-neutral-200/70 dark:bg-neutral-700 dark:hover:bg-neutral-800 w-24 flex justify-center items-center transition-colors">
                    Apply
                  </button>
                </div>
              </div> */}

              <div className="mt-4 flex justify-between py-2.5">
                <span>Subtotal</span>
                <span className="font-semibold text-slate-900 dark:text-slate-200">
                  {`${getCurrencySymbol(getProductCurrency(order?.event))} ${subTotal(orderItems)}`}
                </span>
              </div>
              {/* <div className="flex justify-between py-2.5">
                <span>Shipping estimate</span>
                <span className="font-semibold text-slate-900 dark:text-slate-200">
                  $5.00
                </span>
              </div> */}
              <div className="flex justify-between py-2.5">
                <span>{`VAT/Sales Tax (${getProductTax(order?.event)}%)`}</span>
                <span className="font-semibold text-slate-900 dark:text-slate-200">
                  {`${getCurrencySymbol(getProductCurrency(order?.event))} ${taxEstimated(orderItems)}`}
                </span>
              </div>
              <div className="flex justify-between font-semibold text-slate-900 dark:text-slate-200 text-base pt-4">
                <span>Order total</span>
                {/* <span>£ {totalPrice(orderItems)}</span> */}
                {`${getCurrencySymbol(getProductCurrency(order?.event))} ${totalPrice(orderItems)}`}
              </div>
            </div>
            <div className="mt-8 divide-y divide-slate-200/70 dark:divide-slate-700 ">
              {orderItems?.map(renderProduct)}
            </div>
            {/* <ButtonPrimary href="/order-review" className="mt-8 w-full">
              Confirm order
            </ButtonPrimary> */}
            <div className="mt-5 text-sm text-slate-500 dark:text-slate-400 flex items-center justify-center">
              <p className="block relative pl-5">
                <svg
                  className="w-4 h-4 absolute -left-1 top-0.5"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 8V13"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M11.9945 16H12.0035"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Learn more{` `}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="##"
                  className="text-slate-900 dark:text-slate-200 underline font-medium"
                >
                  Taxes
                </a>
                {/* <span>
                  {` `}and{` `}
                </span>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="##"
                  className="text-slate-900 dark:text-slate-200 underline font-medium"
                >
                  Shipping
                </a> */}
                {` `} infomation
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CheckoutPage;
